import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import { ScrollArea } from "../ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Calendar,
  Play,
  FileText,
  Users,
  Target,
  Edit,
  MoreVertical,
  Plus,
  Sun,
  Sunset,
  Coffee,
  Printer,
  Download,
} from "lucide-react";
import { format, addDays, subDays, isToday, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { getDaySessions, getTimeSlots, exportDayPdf } from "../../lib/api";
import { MATIERES, getMatiereInfo, JournalStatusBadge } from "./constants";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

export default function DayViewDialog({ open, onOpenChange, initialDate, onEditSession, onStartSession }) {
  const [currentDate, setCurrentDate] = useState(initialDate || new Date());
  const [sessions, setSessions] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [exporting, setExporting] = useState(false);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch day data
  const fetchDayData = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = format(currentDate, "yyyy-MM-dd");
      const [sessionsRes, slotsRes] = await Promise.all([
        getDaySessions(dateStr),
        getTimeSlots(),
      ]);
      setSessions(sessionsRes.data?.sessions || []);
      setTimeSlots(slotsRes.data?.slots || []);
    } catch (error) {
      console.error("Error fetching day data:", error);
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    if (open) {
      fetchDayData();
    }
  }, [open, fetchDayData]);

  useEffect(() => {
    if (initialDate) {
      setCurrentDate(initialDate);
    }
  }, [initialDate]);

  const handlePrevDay = () => setCurrentDate(subDays(currentDate, 1));
  const handleNextDay = () => setCurrentDate(addDays(currentDate, 1));
  const handleToday = () => setCurrentDate(new Date());

  // Export day as PDF
  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const dateStr = format(currentDate, "yyyy-MM-dd");
      const response = await exportDayPdf(dateStr);
      
      // Create download link
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cahier-journal-${dateStr}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error exporting PDF:", error);
    } finally {
      setExporting(false);
    }
  };

  // Print day view
  const handlePrint = () => {
    window.print();
  };

  // Calculate position of current time indicator
  const getCurrentTimePosition = () => {
    const now = currentTime;
    const hours = now.getHours();
    const minutes = now.getMinutes();
    
    // Assuming school day is 8:00-17:00 (9 hours)
    const dayStartMinutes = 8 * 60; // 8:00
    const dayEndMinutes = 17 * 60; // 17:00
    const currentMinutes = hours * 60 + minutes;
    
    if (currentMinutes < dayStartMinutes || currentMinutes > dayEndMinutes) {
      return null;
    }
    
    const percentage = ((currentMinutes - dayStartMinutes) / (dayEndMinutes - dayStartMinutes)) * 100;
    return percentage;
  };

  // Group sessions by period (morning/afternoon)
  const groupedSessions = {
    morning: sessions.filter(s => {
      const hour = parseInt(s.start_time?.split(":")[0] || "0");
      return hour < 12;
    }),
    afternoon: sessions.filter(s => {
      const hour = parseInt(s.start_time?.split(":")[0] || "0");
      return hour >= 12;
    }),
  };

  const getSessionDuration = (session) => {
    if (!session.start_time || !session.end_time) return 45;
    const [startH, startM] = session.start_time.split(":").map(Number);
    const [endH, endM] = session.end_time.split(":").map(Number);
    return (endH * 60 + endM) - (startH * 60 + startM);
  };

  const SessionCard = ({ session }) => {
    const matiere = getMatiereInfo(session.matiere);
    const duration = getSessionDuration(session);
    
    return (
      <div
        className="rounded-xl border-2 p-4 transition-all hover:shadow-md"
        style={{ 
          borderColor: matiere?.color || "#e2e8f0",
          backgroundColor: `${matiere?.color || "#f8fafc"}10`,
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            {/* Time and Duration */}
            <div className="flex items-center gap-2 mb-2">
              <Badge 
                variant="outline" 
                className="text-xs font-mono"
                style={{ borderColor: matiere?.color, color: matiere?.color }}
              >
                {session.start_time} - {session.end_time}
              </Badge>
              <Badge variant="secondary" className="text-xs">
                {duration} min
              </Badge>
              <JournalStatusBadge status={session.status} />
            </div>
            
            {/* Title */}
            <h4 className="font-semibold text-slate-800 mb-1">
              {session.title || `Séance de ${matiere?.label || session.matiere}`}
            </h4>
            
            {/* Matière et niveau */}
            <div className="flex items-center gap-2 mb-2">
              <Badge 
                className="text-xs"
                style={{ backgroundColor: matiere?.color, color: "white" }}
              >
                {matiere?.label || session.matiere}
              </Badge>
              {session.niveau && (
                <Badge variant="outline" className="text-xs uppercase">
                  {session.niveau}
                </Badge>
              )}
            </div>
            
            {/* Skills/Objectives */}
            {session.main_skill && (
              <div className="flex items-center gap-1 text-xs text-slate-500 mb-2">
                <Target className="w-3 h-3" />
                <span className="line-clamp-1">{session.main_skill}</span>
              </div>
            )}
            
            {/* Target group */}
            {session.target_type !== "whole_class" && (
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <Users className="w-3 h-3" />
                <span>{session.target_group_name || "Groupe ciblé"}</span>
              </div>
            )}
            
            {/* Notes */}
            {session.notes && (
              <p className="text-xs text-slate-500 mt-2 line-clamp-2 bg-slate-50 rounded p-2">
                {session.notes}
              </p>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex flex-col gap-1">
            <Button
              variant="default"
              size="sm"
              className="gap-1"
              style={{ backgroundColor: matiere?.color }}
              onClick={() => onStartSession?.(session)}
            >
              <Play className="w-3 h-3" />
              Démarrer
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => onEditSession?.(session)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <FileText className="w-4 h-4 mr-2" />
                  Ouvrir ressource
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    );
  };

  const PeriodSection = ({ title, icon: Icon, sessions, color }) => (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <h3 className="font-semibold text-slate-700">{title}</h3>
        <Badge variant="secondary">{sessions.length} séance{sessions.length > 1 ? "s" : ""}</Badge>
      </div>
      
      {sessions.length === 0 ? (
        <div className="text-center py-6 bg-slate-50 rounded-xl border border-dashed border-slate-200">
          <p className="text-sm text-slate-400">Aucune séance prévue</p>
          <Button variant="ghost" size="sm" className="mt-2">
            <Plus className="w-4 h-4 mr-1" />
            Ajouter une séance
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {sessions
            .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""))
            .map(session => (
              <SessionCard key={session.id} session={session} />
            ))
          }
        </div>
      )}
    </div>
  );

  const timePosition = getCurrentTimePosition();
  const isTodayView = isToday(currentDate);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b pb-4">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-500" />
              Vue Jour
            </DialogTitle>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handlePrevDay}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button 
                variant={isTodayView ? "default" : "outline"} 
                size="sm" 
                onClick={handleToday}
              >
                Aujourd'hui
              </Button>
              <Button variant="outline" size="sm" onClick={handleNextDay}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              
              <div className="w-px h-6 bg-slate-200 mx-1" />
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handlePrint}
                title="Imprimer"
              >
                <Printer className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleExportPdf}
                disabled={exporting}
                title="Télécharger PDF"
              >
                {exporting ? (
                  <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
          
          {/* Date display */}
          <div className="flex items-center gap-4 mt-3">
            <div className="text-3xl font-bold text-slate-800">
              {format(currentDate, "d", { locale: fr })}
            </div>
            <div>
              <div className="font-semibold text-slate-700">
                {format(currentDate, "EEEE", { locale: fr })}
              </div>
              <div className="text-sm text-slate-500">
                {format(currentDate, "MMMM yyyy", { locale: fr })}
              </div>
            </div>
            
            {/* Current time indicator */}
            {isTodayView && (
              <Badge className="ml-auto bg-green-500 animate-pulse">
                <Clock className="w-3 h-3 mr-1" />
                {format(currentTime, "HH:mm")}
              </Badge>
            )}
          </div>
        </DialogHeader>
        
        <ScrollArea className="flex-1 pr-4">
          <div className="py-4 relative">
            {/* Current time line indicator */}
            {isTodayView && timePosition !== null && (
              <div 
                className="absolute left-0 right-0 z-10 pointer-events-none"
                style={{ top: `${timePosition}%` }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                  <div className="flex-1 h-0.5 bg-red-500" />
                  <Badge className="bg-red-500 text-xs">
                    {format(currentTime, "HH:mm")}
                  </Badge>
                </div>
              </div>
            )}
            
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-32 w-full" />
                ))}
              </div>
            ) : (
              <>
                {/* Morning */}
                <PeriodSection
                  title="Matin"
                  icon={Sun}
                  sessions={groupedSessions.morning}
                  color="bg-amber-500"
                />
                
                {/* Lunch break indicator */}
                <div className="flex items-center gap-3 my-4 py-2">
                  <div className="flex-1 h-px bg-slate-200" />
                  <div className="flex items-center gap-2 text-slate-400 text-sm">
                    <Coffee className="w-4 h-4" />
                    Pause méridienne
                  </div>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                
                {/* Afternoon */}
                <PeriodSection
                  title="Après-midi"
                  icon={Sunset}
                  sessions={groupedSessions.afternoon}
                  color="bg-indigo-500"
                />
                
                {/* Summary */}
                {sessions.length > 0 && (
                  <div className="mt-6 p-4 bg-slate-50 rounded-xl">
                    <h4 className="text-sm font-semibold text-slate-600 mb-2">Récapitulatif</h4>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(
                        sessions.reduce((acc, s) => {
                          const m = s.matiere || "autre";
                          acc[m] = (acc[m] || 0) + getSessionDuration(s);
                          return acc;
                        }, {})
                      ).map(([matiere, minutes]) => {
                        const info = getMatiereInfo(matiere);
                        return (
                          <Badge 
                            key={matiere}
                            style={{ backgroundColor: info?.color || "#64748b" }}
                            className="text-white"
                          >
                            {info?.label || matiere}: {minutes}min
                          </Badge>
                        );
                      })}
                    </div>
                    <div className="text-xs text-slate-500 mt-2">
                      Total: {sessions.reduce((acc, s) => acc + getSessionDuration(s), 0)} minutes ({sessions.length} séances)
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
